import React from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend,
  type LegendProps,
} from 'recharts';
import { BLUE, CYAN, BLUE_LIGHT, BLUE_DARK } from '../../../constants/chartColors';

const COLORS = [
  BLUE,
  CYAN,
  '#06B6D4',
  BLUE_LIGHT,
  '#5EEAD4',
  BLUE_DARK
];

interface DataPoint {
  name?: string;
  [key: string]: unknown;
}

interface Props {
  type?: string;
  data: DataPoint[];
  dataKey?: string;
  xKey?: string;
  yKey?: string;
  yKeys?: string[];
  color?: string;
  colors?: string[];
  format?: string;
  stacked?: boolean;
}

const ChartWidget: React.FC<Props> = ({
  type = 'bar',
  data,
  dataKey,
  xKey,
  yKey,
  yKeys,
  color,
  colors = COLORS,
  format = 'currency',
  stacked = false
}) => {
  const hasData = (): boolean => {
    if (!data || !Array.isArray(data) || data.length === 0) return false;

    if (['pie', 'donut'].includes(type)) {
      const total = data.reduce((sum, item) => sum + ((item[dataKey ?? 'value'] as number) || 0), 0);
      return total > 0;
    }

    return true;
  };

  if (!hasData()) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-500 dark:text-gray-400 text-sm">No data available</div>
          <div className="text-gray-400 dark:text-gray-500 text-xs mt-1">Data will appear when available</div>
        </div>
      </div>
    );
  }

  const formatValue = (value: number, forTooltip = false): string => {
    if (format === 'percentage') {
      return forTooltip ? `${value.toFixed(1)}%` : `${value.toFixed(0)}%`;
    } else if (format === 'number') {
      return value < 1 ? value.toFixed(2) : value.toFixed(0);
    } else {
      return value < 1 ? `$${value.toFixed(2)}` : `$${value.toFixed(0)}`;
    }
  };

  const renderChart = (): React.ReactElement => {
    switch (type) {
      case 'bar': {
        const barKeysDefault = yKey ? [yKey] : [dataKey ?? 'value'];
        const barKeys = yKeys || barKeysDefault;
        const barColors = colors || COLORS;

        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <defs>
                {barKeys.map((key, index) => (
                  <linearGradient key={`gradient-${key}`} id={`barGradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={barColors[index % barColors.length]} stopOpacity={1} />
                    <stop offset="100%" stopColor={barColors[index % barColors.length]} stopOpacity={0.7} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
              <XAxis
                dataKey={xKey}
                stroke="#9CA3AF"
                tick={{ fill: 'currentColor', fontSize: 10 }}
                interval="preserveStartEnd"
              />
              <YAxis
                stroke="#9CA3AF"
                tick={{ fill: 'currentColor', fontSize: 12 }}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: '#F9FAFB',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)'
                }}
              />
              {barKeys.length > 1 && (
                <Legend
                  wrapperStyle={{ paddingTop: '10px' }}
                  formatter={(value: string) => <span className="text-gray-400 text-xs capitalize">{value}</span>}
                />
              )}
              {barKeys.map((key, index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  fill={`url(#barGradient-${index})`}
                  stackId={stacked ? 'stack' : undefined}
                  radius={stacked ? (index === barKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]) : [4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
      }

      case 'horizontalBar': {
        const truncatedData = data.map(item => ({
          ...item,
          displayName: typeof item.name === 'string' && item.name.length > 20
            ? item.name.substring(0, 20) + '...'
            : item.name,
        }));
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={truncatedData} layout="vertical" margin={{ left: 10 }}>
              <defs>
                <linearGradient id="hBarGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor={color || COLORS[0]} stopOpacity={0.7} />
                  <stop offset="100%" stopColor={color || COLORS[0]} stopOpacity={1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
              <XAxis
                type="number"
                stroke="#9CA3AF"
                tick={{ fill: 'currentColor', fontSize: 12 }}
                tickFormatter={(value: number) => formatValue(value)}
              />
              <YAxis
                dataKey="displayName"
                type="category"
                stroke="#9CA3AF"
                tick={{ fill: 'currentColor', fontSize: 10 }}
                width={120}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: '#F9FAFB',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)'
                }}
                formatter={(value: number) => [formatValue(value, true), format === 'percentage' ? 'Rate' : 'Cost']}
                labelFormatter={(label: string, payload?: Array<{ payload?: DataPoint }>) =>
                  payload?.[0]?.payload?.name || label
                }
              />
              <Bar dataKey={yKey || dataKey || 'value'} fill="url(#hBarGradient)" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );
      }

      case 'line': {
        const lineKeysDefault = yKey ? [yKey] : [dataKey ?? 'value'];
        const lineKeys = yKeys || lineKeysDefault;
        const lineColors = colors || COLORS;

        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
              <XAxis
                dataKey={xKey}
                stroke="#9CA3AF"
                tick={{ fill: 'currentColor', fontSize: 10 }}
                interval="preserveStartEnd"
              />
              <YAxis
                stroke="#9CA3AF"
                tick={{ fill: 'currentColor', fontSize: 12 }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: '#F9FAFB',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)'
                }}
              />
              {lineKeys.length > 1 && (
                <Legend
                  wrapperStyle={{ paddingTop: '10px' }}
                  formatter={(value: string) => <span className="text-gray-400 text-xs capitalize">{value}</span>}
                />
              )}
              {lineKeys.map((key, index) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={lineColors[index % lineColors.length]}
                  strokeWidth={3}
                  dot={{ fill: lineColors[index % lineColors.length], r: 4, strokeWidth: 2, stroke: '#1F2937' }}
                  activeDot={{ r: 6 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );
      }

      case 'area':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <defs>
                <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color || COLORS[0]} stopOpacity={0.8} />
                  <stop offset="95%" stopColor={color || COLORS[0]} stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
              <XAxis
                dataKey={xKey}
                stroke="#9CA3AF"
                tick={{ fill: 'currentColor', fontSize: 12 }}
              />
              <YAxis
                stroke="#9CA3AF"
                tick={{ fill: 'currentColor', fontSize: 12 }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: '#F9FAFB',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)'
                }}
              />
              <Area
                type="monotone"
                dataKey={yKey || dataKey || 'value'}
                stroke={color || COLORS[0]}
                strokeWidth={3}
                fill="url(#areaGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'pie': {
        const pieTotal = data.reduce((sum, item) => sum + ((item[dataKey ?? 'value'] as number) || 0), 0);

        const renderCustomLegend = (props: LegendProps) => {
          const { payload } = props;
          return (
            <div className="flex flex-col gap-1 text-xs max-h-full overflow-y-auto pr-1">
              {(payload ?? []).map((entry, index) => {
                const item = data[index];
                const value = (item?.[dataKey ?? 'value'] as number) || 0;
                const pct = pieTotal > 0 ? ((value / pieTotal) * 100).toFixed(0) : 0;
                const entryValue = String(entry.value);

                return (
                  <div key={`legend-pie-${entryValue}-${index}`} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: colors[index % colors.length] }}
                    />
                    <span className="text-gray-600 dark:text-gray-400 truncate flex-1" title={entryValue}>
                      {entryValue.length > 25 ? entryValue.substring(0, 25) + '...' : entryValue}
                    </span>
                    <span className="text-gray-900 dark:text-white font-semibold whitespace-nowrap">
                      {pct}%
                    </span>
                  </div>
                );
              })}
            </div>
          );
        };

        return (
          <div className="w-full h-full flex">
            <div className="w-1/2 h-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <defs>
                    {colors.map((c, index) => (
                      <radialGradient key={`pieGradient${index}`} id={`pieGradient${index}`}>
                        <stop offset="0%" stopColor={c} stopOpacity={1} />
                        <stop offset="100%" stopColor={c} stopOpacity={0.8} />
                      </radialGradient>
                    ))}
                  </defs>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ percent }: { percent: number }) => `${(percent * 100).toFixed(0)}%`}
                    outerRadius="85%"
                    fill="#8884d8"
                    dataKey={dataKey ?? 'value'}
                    paddingAngle={2}
                    style={{ fontSize: '11px', fontWeight: '600', fill: '#fff' }}
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-pie-${entry.name || index}`}
                        fill={`url(#pieGradient${index % colors.length})`}
                        style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))' }}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: 'none',
                      borderRadius: '0.5rem',
                      color: '#F9FAFB',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)'
                    }}
                    itemStyle={{ color: '#F9FAFB' }}
                    labelStyle={{ color: '#F9FAFB' }}
                    formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="w-1/2 h-full flex items-center pl-2">
              {renderCustomLegend({ payload: data.map((item, i) => ({ value: item.name ?? String(i), color: colors[i % colors.length] })) } as LegendProps)}
            </div>
          </div>
        );
      }

      case 'donut': {
        const donutTotal = data.reduce((sum, item) => sum + ((item[dataKey ?? 'value'] as number) || 0), 0);

        const renderDonutLegend = (props: LegendProps) => {
          const { payload } = props;
          return (
            <div className="flex flex-col gap-1 text-xs max-h-full overflow-y-auto pr-1">
              {(payload ?? []).map((entry, index) => {
                const item = data[index];
                const value = (item?.[dataKey ?? 'value'] as number) || 0;
                const pct = donutTotal > 0 ? ((value / donutTotal) * 100).toFixed(0) : 0;
                const entryValue = String(entry.value);

                return (
                  <div key={`legend-donut-${entryValue}-${index}`} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: colors[index % colors.length] }}
                    />
                    <span className="text-gray-600 dark:text-gray-400 truncate flex-1" title={entryValue}>
                      {entryValue.length > 25 ? entryValue.substring(0, 25) + '...' : entryValue}
                    </span>
                    <span className="text-gray-900 dark:text-white font-semibold whitespace-nowrap">
                      {pct}%
                    </span>
                  </div>
                );
              })}
            </div>
          );
        };

        return (
          <div className="w-full h-full flex">
            <div className="w-1/2 h-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <defs>
                    {colors.map((c, index) => (
                      <radialGradient key={`donutGradient${index}`} id={`donutGradient${index}`}>
                        <stop offset="0%" stopColor={c} stopOpacity={1} />
                        <stop offset="100%" stopColor={c} stopOpacity={0.8} />
                      </radialGradient>
                    ))}
                  </defs>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ percent }: { percent: number }) => `${(percent * 100).toFixed(0)}%`}
                    innerRadius="50%"
                    outerRadius="85%"
                    fill="#8884d8"
                    dataKey={dataKey ?? 'value'}
                    paddingAngle={2}
                    style={{ fontSize: '11px', fontWeight: '600', fill: '#fff' }}
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-donut-${entry.name || index}`}
                        fill={`url(#donutGradient${index % colors.length})`}
                        style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))' }}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: 'none',
                      borderRadius: '0.5rem',
                      color: '#F9FAFB',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)'
                    }}
                    itemStyle={{ color: '#F9FAFB' }}
                    labelStyle={{ color: '#F9FAFB' }}
                    formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="w-1/2 h-full flex items-center pl-2">
              {renderDonutLegend({ payload: data.map((item, i) => ({ value: item.name ?? String(i), color: colors[i % colors.length] })) } as LegendProps)}
            </div>
          </div>
        );
      }

      default:
        return <div className="text-gray-500">Unsupported chart type</div>;
    }
  };

  return (
    <div className="w-full h-full">
      {renderChart()}
    </div>
  );
};

export default ChartWidget;
